/**
 * 按语言的新闻数据源
 * 中文站读 src/data/news.js，英文站读 src/data/news.en.js（结构相同、_id 一致）
 * 语言以 URL 为准（/en 前缀），由路由守卫同步到 i18n locale
 */
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import {newsItems, newsCategories} from '@/data/news';
import {newsItems as newsItemsEn, newsCategories as newsCategoriesEn} from '@/data/news.en';

const SOURCES = {
  'zh-CN': {items: newsItems, categories: newsCategories},
  'en-US': {items: newsItemsEn, categories: newsCategoriesEn}
};

/**
 * 新闻数据 composable：所有返回值随当前语言响应式切换
 * 英文侧缺某个 _id 时详情页落到空态（不回退中文，避免掩盖翻译缺口）
 */
export function useNewsData() {
  const {locale} = useI18n();

  const source = computed(() => SOURCES[locale.value] || SOURCES['zh-CN']);

  // 新闻列表（computed，切换语言自动刷新）
  const items = computed(() => source.value.items);

  // 新闻分类
  const categories = computed(() => source.value.categories);

  // 根据 ID 获取新闻详情
  const getNewsById = (id) => {
    return source.value.items.find(item => item._id === id) || null;
  };

  // 相关新闻推荐（同分类优先，排除当前文章）
  const getRelatedNews = (currentId, limit = 3) => {
    const list = source.value.items;
    const current = getNewsById(currentId);
    if (!current) return list.slice(0, limit);

    const sameCategory = list.filter(
      item => item._id !== currentId && item.category === current.category
    );
    const others = list.filter(
      item => item._id !== currentId && item.category !== current.category
    );

    return [...sameCategory, ...others].slice(0, limit);
  };

  // 获取分类名称
  const getCategoryName = (categoryId) => {
    if (!categoryId) return null;
    const category = source.value.categories.find(c => c.id === categoryId);
    return category ? category.name : null;
  };

  return {
    items,
    categories,
    getNewsById,
    getRelatedNews,
    getCategoryName
  };
}
